import * as CrossFetch from 'cross-fetch';

import Query from '@root/components/query';
import Customers from '@root/customers';
import { Customer, Languages } from '@root/models';


/**
 * The main class for the Unsubscribe page. 
 */
export class Unsubscribe {
    
    private element: Element;
    config: any;
    language: string;
    site: string;
    contact: string;
    contact_type: string;
    contact_key: string;
    unsubscribe_url: string;
    unsubscribe_key: string;
    
    
    /**
     * Constructor for the Unsubscribe class
     * @param {object}
     */
    constructor(element: Element) {
        this.element = element;
    }
    
    
    /**
     * Initialize and setup the associated pages and content.
     */
    async init() {
        
        this.config = await Customers.all();
        this.language = document.documentElement.lang;
        let confirmation_block = this.element.querySelector('#confirmation-block');
        let error_block = this.element.querySelector('#error-block');
        
        // First make sure the backend configuration, which reuses the same format
        // as other customer entries, is available
        let unsub_config = this.config.find((x) => 'unsubscribe_url' in x);
        if (unsub_config) {
            this.unsubscribe_url = unsub_config['unsubscribe_url'];
            this.unsubscribe_key = unsub_config['unsubscribe_key'];
        }
        if (!this.unsubscribe_url || !this.unsubscribe_key) {
            console.error('Missing unsubscribe configuration');
            confirmation_block.classList.add('hidden');
            error_block.classList.remove('hidden');
            // No customer info yet, so not going to redirect to manage link
            error_block.querySelector('.unsubscribe-text3').classList.add('hidden');
            error_block.querySelector('.unsubscribe-text1').innerHTML = this.translate('system_error');
            await this.hideLoader(500);
            return;
        }
        
        // The site is used for both configuring as well as displaying the correct
        // customer name. Every customer should be in customers.json, even embedded
        const site = Query.get('site') || '';
        const private_subscription = site.endsWith('-P');
        // Remove -P and -C from this value for the searching the config but keep
        // it for communication with the backend so we know its their Private
        // or Closed subscription to unsubscribe. Support case insensitive search
        // for customized site values.
        const site_match = site.replace('-P', '').replace('-C', '').toLowerCase();
        let customer = this.config.find((cust: Customer) => cust.code.toLowerCase() === site_match) as Customer;
        if (!customer) {
            console.error('Missing or invalid parameter: site');
            confirmation_block.classList.add('hidden');
            error_block.classList.remove('hidden');
            // No customer info yet, so not going to redirect to manage link
            error_block.querySelector('.unsubscribe-text3').classList.add('hidden');
            error_block.querySelector('.unsubscribe-text1').innerHTML = this.translate('invalid_config');
            await this.hideLoader(500);
            return;
        }
        this.site = site;
        // Default customer name in English, optional if different in French
        this.element.querySelectorAll('.customer-name').forEach((elem) => {
            elem.innerHTML = customer.name;
        });
        // Signup/in URL for this customer
        this.element.querySelectorAll('.customer-link').forEach((elem) => {
            elem.setAttribute('href', './index.html?site=' + site);
        });
        // Support alternate signup pages for specific customers
        if (customer['alt_signup_url_en']) {
            this.element.querySelectorAll('.customer-link').forEach((elem) => {
                elem.setAttribute('href', customer['alt_signup_url_en']);
            });
        }
        if (this.language === Languages.French) {
            if (customer['name_fr']) {
                this.element.querySelectorAll('.customer-name').forEach((elem) => {
                    elem.innerHTML = customer['name_fr'];
                });
            }
            this.element.querySelectorAll('.customer-link').forEach((elem) => {
                elem.setAttribute('href', './index-fr.html?site=' + site);
            });
            if (customer['alt_signup_url_fr']) {
                this.element.querySelectorAll('.customer-link').forEach((elem) => {
                    elem.setAttribute('href', customer['alt_signup_url_fr']);
                });
            }
        }
        // Don't show the account management links for Private/Internal
        if (private_subscription) {
            confirmation_block.querySelector('.unsubscribe-text2').classList.add('hidden');
            error_block.querySelector('.unsubscribe-text3').classList.add('hidden');
        }
        
        // Contact info and the unique unsubscribe key
        const email = Query.get('email');
        const sms = Query.get('sms');
        let contact = '';
        let contact_type = '';
        let type_label = 'email';
        if (email) {
            contact = email;
            contact_type = 'email';
        } else if (sms) {
            contact = sms;
            contact_type = 'sms';
            type_label = 'SMS text';
        } else {
            console.error('Missing parameter: email or sms');
        }
        const contact_key = Query.get('key');
        if (!contact_key) console.error('Missing parameter: key');
        if (!contact || !contact_key) {
            confirmation_block.classList.add('hidden');
            error_block.classList.remove('hidden');
            error_block.querySelector('.unsubscribe-text1').innerHTML = this.translate('unknown_account');
            await this.hideLoader(500);
            return;
        }
        
        this.element.querySelectorAll('.contact-type').forEach((elem) => {
            elem.innerHTML = type_label;
        });
        this.element.querySelectorAll('.alternate-type').forEach((elem) => {
            if (contact_type == 'sms' ) {
                elem.innerHTML = this.language === Languages.French ? 'numéro de SMS' : 'SMS number';
            } else {
                elem.innerHTML = this.language === Languages.French ? 'adresse email' : 'email address';
            }
        });
        this.element.querySelectorAll('.user-contact').forEach((elem) => {
            if (contact_type == 'sms') {
                elem.innerHTML = this.reformat_phone(contact);
            } else {
                elem.innerHTML = contact;
            }
        });
        this.contact = contact;
        this.contact_type = contact_type;
        this.contact_key = contact_key;
        
        // Now that we've validated the parameters locally, also validate with the
        // backend and handle any errors that it finds
        let check_result = await this.check_params();
        if (!check_result) {
            await this.hideLoader(900);
            return;
        }
        
        // Ready for the user to confirm
        this.element.querySelector('#unsubscribe-button').addEventListener(
            'click', () => this.confirm_unsubscribe());
        await this.hideLoader(900);
    }
    
    
    /**
     * Send requests to the backend API. Similar to src/communication.ts but
     * removed the call to handleResponse which is not needed.
     */
    async fetch(url: string, params?: any) : Promise<Response | null> {
        let response: Response | null = null;
        try {
            response = await CrossFetch.fetch(url, params || { method: 'GET' });
        } catch (e) {
            console.error(e);
        }
        
        return response;
    }
    
    
    /**
     * Get the content and translation for feedback text strings. Simplified method
     * with focused content instead of components/language.ts
     */
    private translate(key: string) : string {
        const translations = {
            "en": {
                "system_error": "System configuration error, contact info@peasi.com for assistance.",
                "invalid_config": "Invalid configuration, contact info@peasi.com for assistance.",
                "unknown_account": "Unknown contact.",
                "apiEmailAddressInvalid": "Unknown email address.",
                "apiEmailKeyInvalid": "The email address is not recognized. Try managing alerts instead.",
                "apiPhoneNumberInvalid": "Unknown phone number.",
                "apiPhoneKeyInvalid": "The phone number is not recognized. Try managing alerts instead.",
                "apiDuplicateRequest": "This contact has already been unsubscribed."
            },
            "fr": {
                "system_error": "Erreur de configuration du systeme, contactez info@peasi.com pour obtenir de l'aide.",
                "invalid_config": "Configuration invalide, contactez info@peasi.com pour obtenir de l'aide.",
                "unknown_account": "Contact inconnu.",
                "apiEmailAddressInvalid": "Adresse email inconnue.",
                "apiEmailKeyInvalid": "L'adresse email n'est pas reconnue. Essayez plutôt de gérer les alertes.",
                "apiPhoneNumberInvalid": "Numéro de téléphone inconnu.",
                "apiPhoneKeyInvalid": "Le numéro de téléphone n'est pas reconnu. Essayez plutôt de gérer les alertes.",
                "apiDuplicateRequest": "Cette contact a déjà été désabonnée."
            }
        };
        let result = translations[this.language][key];
        if (!result) {
            result = 'An unexpected error has occurred. Please try again later.';
        }
        
        return result;
    }
    
    
    /**
     * Validate the query parameters with the API backend
     */
    async check_params() : Promise<boolean> {
        let success = false;
        let response_body = '';
        try {
            const body = new FormData();
            body.append('site', this.site);
            body.append(this.contact_type, this.contact);
            body.append('key', this.contact_key);
            const response = await this.fetch(this.unsubscribe_url, {
                method: 'POST',
                body,
                headers: {
                    'Authorization': 'Token ' + this.unsubscribe_key
                },
                mode: 'cors'
            });
            success = response && response.status === 200;
            if (!success) {
                response_body = await response.json();
            }
        } catch (e) {
            console.error(e);
        }
        
        if (!success) {
            let confirmation_block = this.element.querySelector('#confirmation-block');
            let error_block = this.element.querySelector('#error-block');
            confirmation_block.classList.add('hidden');
            error_block.classList.remove('hidden');
            error_block.querySelector('.unsubscribe-text1').innerHTML = this.translate(response_body);
        }
        
        return success;
    }
    
    
    /**
     * Hide the loading overlay after a short delay to allow loading to finish.
     */
    private async hideLoader(delay: number) {
        setTimeout(() => {
            this.element.querySelector("#unsubscribe-loader").classList.add('hidden');
        }, delay);
    }
    
    
    /**
     * Confirm and process the unsubcribe with the backend.
     */
    async confirm_unsubscribe() : Promise<boolean> {
        let success = false;
        let response_body = '';
        try {
            const body = new FormData();
            body.append('site', this.site);
            body.append(this.contact_type, this.contact);
            body.append('key', this.contact_key);
            body.append('confirm', 'y');
            const response = await this.fetch(this.unsubscribe_url, {
                method: 'POST',
                body,
                headers: {
                    'Authorization': 'Token ' + this.unsubscribe_key
                },
                mode: 'cors'
            });
            success = response && response.status === 201;
            if (!success) {
                response_body = await response.json();
            }
        } catch (e) {
            console.error(e);
        }
        
        let confirmation_block = this.element.querySelector('#confirmation-block');
        let result_block = this.element.querySelector('#result-block');
        let error_block = this.element.querySelector('#error-block');
        if (success) {
            confirmation_block.classList.add('hidden');
            result_block.classList.remove('hidden');
        } else {
            confirmation_block.classList.add('hidden');
            error_block.classList.remove('hidden');
            error_block.querySelector('.unsubscribe-text1').innerHTML = this.translate(response_body);
        }
        
        return success;
    }
    
    
    /**
     * Reformat a phone number from 222333444 to 222-333-4444
     */
    private reformat_phone(phone: string) : string {
        let match = phone.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return match[1] + '-' + match[2] + '-' + match[3];
        } else {
            return phone;
        }
    }
    
}